"use client";

import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { track } from "@/libs/analytics";
import { useCreditsContext } from "@/contexts/CreditsContext";
import CrossSellSitewideBanner from "@/components/cross-sell/CrossSellSitewideBanner";
import { useCrossSellOffer } from "@/hooks/useCrossSellOffer";
import {
  crossSellOfferFromSitewideBannerId,
  getCrossSellOffer,
  isCrossSellSitewideBannerId,
} from "@/libs/cross-sell-v2";

/**
 * Swap active sitewide banner here.
 * Cross-sell v2 preview: xsell_companion | xsell_looksmax | xsell_ai-photo-editor
 */
export const ACTIVE_SITEVIDE_BANNER: SitewideBannerId = "deal";

type SitewideBannerId = "deal" | "peeqo" | "xsell_companion" | "xsell_looksmax" | "xsell_ai-photo-editor";

/** Returns ms until next local midnight — resets naturally every day at 00:00. */
function getMidnightExpiry(): number {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight.getTime();
}

function formatCountdown(ms: number) {
  if (ms <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  const totalSec = Math.floor(ms / 1000);
  return {
    d: Math.floor(totalSec / 86400),
    h: Math.floor((totalSec % 86400) / 3600),
    m: Math.floor((totalSec % 3600) / 60),
    s: totalSec % 60,
  };
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

/**
 * Sitewide banner slot — renders the active banner variant.
 * Both variants are kept in this file for easy swapping via ACTIVE_SITEVIDE_BANNER.
 */
export default function PromoBanner() {
  const { offer } = useCrossSellOffer();

  if (offer) {
    return <CrossSellSitewideBanner offer={offer} />;
  }

  if (isCrossSellSitewideBannerId(ACTIVE_SITEVIDE_BANNER)) {
    const offer = getCrossSellOffer(crossSellOfferFromSitewideBannerId(ACTIVE_SITEVIDE_BANNER));
    return <CrossSellSitewideBanner offer={offer} />;
  }

  const banners: Record<Exclude<SitewideBannerId, `xsell_${string}`>, () => JSX.Element | null> = {
    deal: DealPromoBanner,
    peeqo: PeeqoPromoBanner,
  };

  const Banner = banners[ACTIVE_SITEVIDE_BANNER as Exclude<SitewideBannerId, `xsell_${string}`>];
  return <Banner />;
}

/**
 * Deal Promo Banner — evergreen 35% OFF Pro offer.
 * Every new visitor gets a fresh 24 h countdown. Hidden for active subscribers.
 */
function DealPromoBanner() {
  const { subscriptionStatus, isGuest, isLoading } = useCreditsContext();
  const [mounted, setMounted] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const expiryRef = useRef<number>(0);
  const pathname = usePathname();

  useEffect(() => {
    expiryRef.current = getMidnightExpiry();
    setRemaining(expiryRef.current - Date.now());
    setMounted(true);

    const id = setInterval(() => {
      const left = expiryRef.current - Date.now();
      if (left <= 0) {
        expiryRef.current = getMidnightExpiry();
        setRemaining(expiryRef.current - Date.now());
      } else {
        setRemaining(left);
      }
    }, 1000);

    return () => clearInterval(id);
  }, []);

  const isSubscriptionChecked = isGuest || !isLoading;
  const hasActiveSubscription = subscriptionStatus === "active";

  const shouldShow =
    mounted &&
    !isDismissed &&
    isSubscriptionChecked &&
    !hasActiveSubscription;

  useEffect(() => {
    if (shouldShow) {
      track("promo_banner_impression", "monetization", {
        page: pathname,
        banner: "deal",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow]);

  if (!shouldShow) return null;

  const { d, h, m, s } = formatCountdown(remaining);

  function handleCtaClick() {
    track("promo_banner_cta_clicked", "monetization", {
      page: pathname,
      banner: "deal",
    });
  }

  function handleDismiss() {
    setIsDismissed(true);
    track("promo_banner_dismissed", "monetization", {
      page: pathname,
      banner: "deal",
    });
  }

  return (
    <div
      data-promo-banner
      className="relative z-50 flex items-center justify-center px-10"
      style={{
        height: "40px",
        background: "linear-gradient(90deg, #ff6b35 0%, #f7931e 30%, #ffcd3c 60%, #ff6b9d 100%)",
      }}
    >
      <div className="flex flex-nowrap items-center gap-2 text-sm font-medium text-gray-900">
        <span className="font-black text-[0.75rem] whitespace-nowrap sm:hidden">35% OFF</span>

        <span
          className="hidden sm:inline-flex items-center text-white gap-1 bg-white/30 backdrop-blur-sm px-2 py-0.5 rounded-full text-xs font-black tracking-wide whitespace-nowrap"
          style={{ background: "linear-gradient(to right, rgb(236 72 153), rgb(217 70 239))" }}
        >
          🔥 LAST CHANCE
        </span>

        <span
          className="inline-flex items-center gap-1 bg-white/30 backdrop-blur-sm px-2 py-0.5 rounded-full text-white text-xs font-semibold whitespace-nowrap"
          style={{ background: "linear-gradient(to right, rgb(236 72 153), rgb(217 70 239))" }}
        >
          <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth="2"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6l4 2"/>
          </svg>
          <span className="sm:hidden tabular-nums">Expires in {d}d {pad(h)}h {pad(m)}m {pad(s)}s</span>
          <span className="hidden sm:inline tabular-nums">Discount Expires in {d}d {pad(h)}h {pad(m)}m {pad(s)}s</span>
        </span>

        <span className="hidden sm:inline font-semibold whitespace-nowrap">
          <span className="hidden md:inline">New AI Chat Editor · No blurred photos after 100+ edits · </span>
          {/*<span className="hidden md:inline">New AI Headshots &amp; Viral Selfies · </span>*/}
          <span className="font-black">35% OFF</span>
        </span>

        <Link
          href="/dashboard/pricing"
          onClick={handleCtaClick}
          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap transition-opacity hover:opacity-90 shadow-sm"
          style={{ background: "linear-gradient(to right, rgb(236 72 153), rgb(217 70 239))" }}
        >
          Try now
        </Link>
      </div>

      <button
        onClick={handleDismiss}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-700 hover:bg-black/10 transition-colors"
        aria-label="Close"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/**
 * PEEQO cross-promo banner — Instagram Followers Tracker.
 * Shown sitewide to all visitors.
 */
function PeeqoPromoBanner() {
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      track("promo_banner_impression", "monetization", {
        page: pathname,
        banner: "peeqo",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  if (!mounted) return null;

  function handleCtaClick() {
    track("promo_banner_cta_clicked", "monetization", {
      page: pathname,
      banner: "peeqo",
    });
  }

  return (
    <div
      data-promo-banner
      className="relative z-50 flex items-center justify-center px-3 sm:px-6"
      style={{
        height: "40px",
        background: "linear-gradient(105deg, #ef5f7e, #f0577c 45%, #8b5cf6 130%)",
      }}
    >
      <div className="flex w-full max-w-6xl items-center justify-center gap-2 sm:gap-3 text-sm text-white">
        <span className="inline-flex shrink-0 items-center rounded-full bg-white/25 px-2 py-0.5 text-[0.65rem] font-black tracking-wide backdrop-blur-sm sm:px-2.5 sm:text-xs">
          NEW
        </span>

        <span className="min-w-0 text-center text-xs font-medium leading-tight sm:text-sm">
          <span className="sm:hidden"><span className="font-bold">Instagram Activity Tracker</span></span>
          <span className="hidden sm:inline">
            <span className="font-bold">Instagram Activity Tracker</span> &bull; See anyone's followers, unfollowers &amp; likes
          </span>
        </span>

        <a
          href="https://peeqd.app"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleCtaClick}
          className="inline-flex shrink-0 items-center rounded-full bg-white px-3 py-1 text-xs font-bold whitespace-nowrap transition-opacity hover:opacity-90"
          style={{ color: "#db2777" }}
        >
          Search now &rarr;
        </a>
      </div>
    </div>
  );
}
